/** Format helpers — locale and currency aware. ALWAYS uses Latin (Western
 *  Arabic) digits 0-9, even when the Arabic locale would normally render
 *  Eastern Arabic digits (٠-٩). The user requirement is: numbers stay in
 *  Latin digits regardless of locale. */

const LATIN: Intl.NumberFormatOptions = { numberingSystem: 'latn' };
const LATIN_DT: Intl.DateTimeFormatOptions = { numberingSystem: 'latn' };

/**
 * The store's currency ISO code (e.g. 'EGP', 'SAR', 'USD'). Set once at app
 * startup from the backend (`/api/app_currency`). Every `formatMoney` call
 * without an explicit currency uses this, so changing the currency in Settings
 * is reflected across the whole system.
 */
let APP_CURRENCY = 'EGP';

export function setAppCurrency(code?: string | null): void {
  if (code && typeof code === 'string') APP_CURRENCY = code;
}

export function getAppCurrency(): string {
  return APP_CURRENCY;
}

export function formatMoney(amount: number, currency?: string, locale = 'ar-EG'): string {
  const cur = currency || APP_CURRENCY;
  try {
    return new Intl.NumberFormat(locale, { ...LATIN, style: 'currency', currency: cur }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${cur}`;
  }
}

export function formatNumber(value: number, locale = 'ar-EG'): string {
  return new Intl.NumberFormat(locale, LATIN).format(value);
}

/**
 * The store's date display pattern from Settings → Localization
 * (YYYY-MM-DD, DD/MM/YYYY, …). Empty pattern falls back to the locale's
 * medium date style. Set at app startup alongside the currency.
 */
let APP_DATE_FORMAT = '';

export function setAppDateFormat(pattern?: string | null): void {
  if (typeof pattern === 'string') APP_DATE_FORMAT = pattern.trim();
}

/** Render a Date through the configured YYYY/MM/DD-token pattern. */
function applyDatePattern(d: Date, pattern: string): string {
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return pattern.replace('YYYY', yyyy).replace('MM', mm).replace('DD', dd);
}

export function formatDate(value: Date | string, locale = 'ar-EG'): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  if (APP_DATE_FORMAT) return applyDatePattern(d, APP_DATE_FORMAT);
  return new Intl.DateTimeFormat(locale, { ...LATIN_DT, dateStyle: 'medium' }).format(d);
}

export function formatTime(value: Date | string, locale = 'ar-EG'): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(locale, { ...LATIN_DT, timeStyle: 'short' }).format(d);
}

export function formatDateTime(value: Date | string, locale = 'ar-EG'): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  if (APP_DATE_FORMAT) {
    return `${applyDatePattern(d, APP_DATE_FORMAT)} ${formatTime(d, locale)}`;
  }
  return new Intl.DateTimeFormat(locale, {
    ...LATIN_DT,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}
